import type { Vec2 } from '../shared/types';
import {
  BAND_MITRE,
  offsetPolyline as offsetLine,
} from './geometry';

/** Unit left-hand normal (+90°, CCW) of the edge a→b. */
function leftNormal(a: Vec2, b: Vec2): Vec2 {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.hypot(dx, dy) || 1;
  return { x: -dy / len, y: dx / len };
}

/** A polyline whose last point sits on its first (within 1 mm) is CLOSED: a ring,
 *  every corner — the joint included — mitred, no end caps. 3+ distinct points. */
export function polylineIsClosed(pts: readonly Vec2[]): boolean {
  return pts.length > 3 && Math.hypot(pts[0].x - pts[pts.length - 1].x, pts[0].y - pts[pts.length - 1].y) < 1;
}

/**
 * Drag vertex `i` of a wall / line polyline to `q`. On a CLOSED one (`closed`, as
 * it was when the drag began) the two ends are one joint and move together; on an
 * open one an end dropped within `tol` of the other end lands exactly on it, which
 * closes it. Deciding by the state at the press is what lets the drag that just
 * closed a polyline still move on past the joint.
 */
export function dragPolylineVertex(pts: Vec2[], i: number, q: Vec2, closed: boolean, tol: number): void {
  const last = pts.length - 1;
  const end = i === 0 || i === last;
  if (closed && end) { pts[0] = { ...q }; pts[last] = { ...q }; return; }
  if (!closed && end && pts.length >= 4) {
    const other = pts[i === 0 ? last : 0];
    if (Math.hypot(q.x - other.x, q.y - other.y) <= tol) { pts[i] = { ...other }; return; }
  }
  pts[i] = q;
}

/** What a length pin is stored on: a wall / line's centre polyline and its
 *  per-segment pins (`Wall.segLengths`). */
export interface PinnedPolyline { points: Vec2[]; segLengths?: Array<number | null> }

/** A stored pin that no longer matches its segment (by more than this, mm) came
 *  from an edit that does not honour pins (trim, fillet, scale…): it reads as none. */
export const PIN_TOL = 0.5;

/** The pinned length of segment `s` (points[s] → points[s+1]), or null. A list of
 *  the wrong size (a vertex count changed by an edit that does not renumber it) or
 *  a figure the geometry has left behind pins nothing. */
export function wallSegPin(w: PinnedPolyline, s: number): number | null {
  const lens = w.segLengths, pts = w.points;
  if (!lens || lens.length !== pts.length - 1 || s < 0 || s >= lens.length) return null;
  const v = lens[s];
  if (v == null || !Number.isFinite(v)) return null;
  return Math.abs(Math.hypot(pts[s + 1].x - pts[s].x, pts[s + 1].y - pts[s].y) - v) <= PIN_TOL ? v : null;
}

/** Every segment's pin (null where none) — the list the padlocks are drawn from. */
export function wallSegPins(w: PinnedPolyline): Array<number | null> {
  return w.points.slice(1).map((_, s) => wallSegPin(w, s));
}

/** Pin segment `s` at `len` (null = release it). Stale entries are dropped with it,
 *  and an empty list is removed. */
export function setWallSegPin(w: PinnedPolyline, s: number, len: number | null): void {
  const next = wallSegPins(w);
  if (s >= 0 && s < next.length) next[s] = len == null ? null : Math.round(len * 1000) / 1000;
  if (next.some((v) => v != null)) w.segLengths = next; else delete w.segLengths;
}

/**
 * Where vertex `i` may go toward `q` while its pinned segments keep their length:
 * on the circle about the far end of one pinned segment, where the two circles
 * meet (the crossing nearest `q`) for two, `q` itself for none. Null when the two
 * circles do not meet — the vertex holds where it is. A closed polyline's joint
 * (`closed`, both ends) is one vertex between the first and the last segment.
 */
export function holdPinnedVertex(w: PinnedPolyline, i: number, q: Vec2, closed: boolean): Vec2 | null {
  const pts = w.points, last = pts.length - 1;
  const joint = closed && (i === 0 || i === last);
  const pins: Array<{ c: Vec2; r: number }> = [];
  const add = (s: number, far: number): void => { const r = wallSegPin(w, s); if (r != null) pins.push({ c: pts[far], r }); };
  if (joint) { add(0, 1); add(last - 1, last - 1); }
  else {
    if (i > 0) add(i - 1, i - 1);
    if (i < last) add(i, i + 1);
  }
  if (!pins.length) return q;
  const onCircle = (c: Vec2, r: number, p: Vec2): Vec2 => {
    const dx = p.x - c.x, dy = p.y - c.y, d = Math.hypot(dx, dy);
    if (d < 1e-9) return pts[i];
    return { x: c.x + (dx / d) * r, y: c.y + (dy / d) * r };
  };
  if (pins.length === 1) return onCircle(pins[0].c, pins[0].r, q);
  const [A, B] = pins;
  const dx = B.c.x - A.c.x, dy = B.c.y - A.c.y, d = Math.hypot(dx, dy);
  if (d < 1e-9) return Math.abs(A.r - B.r) <= 1e-6 ? onCircle(A.c, A.r, q) : null;
  if (d > A.r + B.r + 1e-9 || d < Math.abs(A.r - B.r) - 1e-9) return null;
  const a = (A.r * A.r - B.r * B.r + d * d) / (2 * d);
  const h = Math.sqrt(Math.max(0, A.r * A.r - a * a));
  const mx = A.c.x + (dx / d) * a, my = A.c.y + (dy / d) * a;
  const p1 = { x: mx - (dy / d) * h, y: my + (dx / d) * h };
  const p2 = { x: mx + (dy / d) * h, y: my - (dx / d) * h };
  return Math.hypot(p1.x - q.x, p1.y - q.y) <= Math.hypot(p2.x - q.x, p2.y - q.y) ? p1 : p2;
}

/**
 * `dragPolylineVertex` that holds the polyline's pinned lengths: the vertex goes
 * where `holdPinnedVertex` allows (an end held by a pin never jumps onto the other
 * end to close it). False when it cannot move at all.
 */
export function dragPinnedVertex(w: PinnedPolyline, i: number, q: Vec2, closed: boolean, tol: number): boolean {
  const held = holdPinnedVertex(w, i, q, closed);
  if (!held) return false;
  if (held === q) { dragPolylineVertex(w.points, i, q, closed, tol); return true; }
  const last = w.points.length - 1;
  if (closed && (i === 0 || i === last)) { w.points[0] = { ...held }; w.points[last] = { ...held }; }
  else w.points[i] = held;
  return true;
}

/** A wall's / line's centre line offset to its left by `dist` (negative: right),
 *  mitred at every vertex — a closed one's (its first point repeated last) at its
 *  joint too, the repeat kept. */
function offsetPolyline(pts: Vec2[], dist: number): Vec2[] {
  if (!polylineIsClosed(pts)) return offsetLine(pts, dist, false, BAND_MITRE);
  const out = offsetLine(pts.slice(0, -1), dist, true, BAND_MITRE);
  out.push({ ...out[0] });
  return out;
}

/** The closed band polygon of a wall (centre polyline + thickness + alignment). */
export function wallBand(pts: Vec2[], thickness: number, align: 'center' | 'left' | 'right'): Vec2[] {
  if (pts.length < 2) return [];
  const t = Math.max(0, thickness);
  const oL = align === 'center' ? t / 2 : align === 'left' ? t : 0;
  const oR = align === 'center' ? t / 2 : align === 'right' ? t : 0;
  const left = offsetPolyline(pts, oL);
  const right = offsetPolyline(pts, -oR);
  return [...left, ...right.reverse()];
}

type WallLike = { points: Vec2[]; thickness: number; align: 'center' | 'left' | 'right'; style?: string };

/** A drawn wall's two long faces, segment by segment, each with the unit normal
 *  pointing away from the wall — what a part seats flush against (a junction
 *  box on a plant room's wall). A line has none. */
export function wallFaces(w: WallLike): Array<{ a: Vec2; b: Vec2; n: Vec2 }> {
  if (w.style === 'line' || w.thickness <= 0 || w.points.length < 2) return [];
  const t = w.thickness;
  const oL = w.align === 'center' ? t / 2 : w.align === 'left' ? t : 0;
  const oR = w.align === 'center' ? t / 2 : w.align === 'right' ? t : 0;
  const left = offsetPolyline(w.points, oL), right = offsetPolyline(w.points, -oR);
  const out: Array<{ a: Vec2; b: Vec2; n: Vec2 }> = [];
  for (let i = 0; i + 1 < w.points.length; i++) {
    if (Math.hypot(w.points[i + 1].x - w.points[i].x, w.points[i + 1].y - w.points[i].y) < 1e-6) continue;
    const n = leftNormal(w.points[i], w.points[i + 1]);
    out.push({ a: left[i], b: left[i + 1], n }, { a: right[i], b: right[i + 1], n: { x: -n.x, y: -n.y } });
  }
  return out;
}

/** A wall's two face corners at its vertex `i`: [left face, right face] — where the
 *  band `wallBand` draws them, so a left / right aligned wall's faces sit on its
 *  own side of the drawn line. */
export function wallFaceCorners(w: WallLike, i: number): [Vec2, Vec2] {
  const band = wallBand(w.points, w.thickness, w.align);
  const n = w.points.length;
  return [band[i], band[2 * n - 1 - i]];
}

/** Every vertex a wall snaps BY when it is dragged: its drawn line and both
 *  faces' corners, without repeats (a left / right aligned wall's line IS one of
 *  its faces). A line has no faces. */
export function wallSnapVerts(w: WallLike): Vec2[] {
  const all = w.style === 'line' || w.thickness <= 0 ? w.points : [...w.points, ...wallBand(w.points, w.thickness, w.align)];
  const out: Vec2[] = [];
  for (const p of all) if (!out.some((q) => Math.hypot(q.x - p.x, q.y - p.y) < 1e-6)) out.push({ x: p.x, y: p.y });
  return out;
}

/** Vertex `i` of a wall dragged to `node`: when one of its FACE corners there
 *  lies nearer a target (within `thr`) than the node itself does, the node moves
 *  so that corner lands exactly on the target — re-measured, since a corner's
 *  mitre turns as the node moves. Null when the node's own snap is the better
 *  one, or nothing is in reach. */
export function wallFaceSnap(
  w: WallLike, i: number, node: Vec2, targets: Vec2[], thr: number, nodeDist = Infinity,
): { node: Vec2; at: Vec2 } | null {
  if (w.style === 'line' || w.thickness <= 0) return null;
  const at = (p: Vec2): WallLike => ({ ...w, points: w.points.map((q, k) => (k === i ? p : q)) });
  let best: { side: 0 | 1; t: Vec2; d: number } | null = null;
  const corners = wallFaceCorners(at(node), i);
  corners.forEach((c, side) => {
    for (const t of targets) {
      const d = Math.hypot(c.x - t.x, c.y - t.y);
      if (d < thr && d < nodeDist && (!best || d < best.d)) best = { side: side as 0 | 1, t, d };
    }
  });
  if (!best) return null;
  const { side, t } = best as { side: 0 | 1; t: Vec2; d: number };
  let p = node;
  for (let k = 0; k < 4; k++) {
    const c = wallFaceCorners(at(p), i)[side];
    p = { x: p.x + (t.x - c.x), y: p.y + (t.y - c.y) };
  }
  return { node: p, at: { x: t.x, y: t.y } };
}
